import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Sheet } from "@/components/ui/Sheet";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  DocumentResource,
  EquipmentResource,
  Page,
  PropertyListItemResource,
  ReferenceListItemResource,
  UnitResource,
} from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";
import { useIsPhone } from "@/lib/use-media-query";

/**
 * The phone-first capture form (Story 12.1, R-E1, spec judgement call 1) —
 * shared between `Equipment.tsx`'s own portfolio-wide "Add equipment"
 * action (property chosen explicitly) and `PropertyRecord.tsx`'s own "Add
 * equipment" action (`defaultPropertyId` pre-selects that property, mirrors
 * spec judgement call 2's "at minimum: a button on the property record page
 * ... and one on the portfolio-wide equipment list").
 *
 * A single screen, not a multi-step wizard (AC: "never a multi-screen
 * wizard for the common case") — Identity + Location render immediately;
 * Purchase/Warranty/Lifecycle/Service sit behind one "More details"
 * disclosure, mirroring `RecordPage.tsx`'s own identical toggle. This is
 * the "under 60 seconds" discipline (R-E1) as real UX restraint, not a
 * literal timer: photo the data plate, pick a category, type the serial,
 * save — everything else is there for the Owner who wants it, never forced
 * open by default.
 *
 * The building-level choice (R-P7, AD-18) is a plain "(Building-level, no
 * specific unit)" option in the unit `<select>` — the frontend never learns
 * a property's own sentinel unit id; submitting `unit_id: null` for that
 * choice lets the server resolve it (`app/api/equipment.py::create_
 * equipment`).
 *
 * Photos (the data-plate photo and, separately, a photo of the unit)
 * upload via the existing polymorphic document mechanism, exactly like
 * `AddDocumentDialog.tsx` — never a bespoke equipment-only upload path.
 * Both are optional and uploaded only after the equipment row itself is
 * created (a document needs a real `entity_id` to attach to); an upload
 * failure surfaces its own message but never rolls back — or blocks — the
 * equipment record that already saved successfully.
 *
 * Picks `Dialog` (desktop) or `Sheet` (phone) by viewport, mirroring
 * `ConfirmDialog.tsx`'s own established "same content, different container
 * per posture" pattern (`Dialog.tsx`'s own doc comment: it is desktop-only
 * styling by design, and every caller is expected to branch) — found and
 * fixed live, verifying R-E1's own explicit "from a phone" requirement on a
 * real phone-sized viewport: this form's own field count made it the first
 * caller in the codebase tall enough to expose both this missed branch and
 * a pre-existing unbounded-height gap in `Dialog.tsx`/`Sheet.tsx`
 * themselves (fixed there too, as its own gap-fill).
 */

const NO_SPECIFIC_UNIT_VALUE = "";

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

export function AddEquipmentDialog({
  open,
  onOpenChange,
  onCreated,
  defaultPropertyId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
  defaultPropertyId?: string;
}) {
  const [propertyId, setPropertyId] = useState(defaultPropertyId ?? "");
  const [unitId, setUnitId] = useState(NO_SPECIFIC_UNIT_VALUE);
  const [category, setCategory] = useState("");
  const [make, setMake] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [capacity, setCapacity] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [specificLocation, setSpecificLocation] = useState("");
  const [dataPlatePhoto, setDataPlatePhoto] = useState<File | null>(null);
  const [unitPhoto, setUnitPhoto] = useState<File | null>(null);

  // Purchase/Warranty/Lifecycle/Service — behind "More details" (module
  // doc comment). `usefulLifeTouched` tracks whether the Owner has typed
  // their own value, so a later category change never silently overwrites
  // it (VC-12.1-04's own third assertion).
  const [moreOpen, setMoreOpen] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [retailerOrInstaller, setRetailerOrInstaller] = useState("");
  const [warrantyType, setWarrantyType] = useState("");
  const [warrantyLength, setWarrantyLength] = useState("");
  const [warrantyExpirationDate, setWarrantyExpirationDate] = useState("");
  const [hasExtendedWarranty, setHasExtendedWarranty] = useState("");
  const [claimPhoneNumber, setClaimPhoneNumber] = useState("");
  const [usefulLifeYears, setUsefulLifeYears] = useState("");
  const [usefulLifeTouched, setUsefulLifeTouched] = useState(false);
  const [replacementCostEstimate, setReplacementCostEstimate] = useState("");
  const [condition, setCondition] = useState("");
  const [serviceInterval, setServiceInterval] = useState("");
  const [serviceIntervalMonths, setServiceIntervalMonths] = useState("");
  const [lastServiceDate, setLastServiceDate] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  //: Module doc comment's own "picks Dialog/Sheet by viewport" gap-fill —
  //: mirrors `ConfirmDialog.tsx`'s identical `isPhone ? Sheet : Dialog`.
  const isPhone = useIsPhone();
  const Container = isPhone ? Sheet : Dialog;

  const propertiesQuery = useQuery({
    queryKey: ["properties", "list", "for-equipment-capture"],
    queryFn: () => apiFetch<Page<PropertyListItemResource>>("/properties?page_size=200"),
    enabled: open,
  });
  const unitsQuery = useQuery({
    queryKey: ["properties", propertyId, "units", "for-equipment-capture"],
    queryFn: () => apiFetch<Page<UnitResource>>(`/properties/${propertyId}/units`),
    enabled: open && propertyId !== "",
  });
  const categoriesQuery = useQuery({
    queryKey: ["reference-list-items", "equipment_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=equipment_category"),
    enabled: open,
  });
  const usefulLifeDefaultsQuery = useQuery({
    queryKey: ["equipment", "useful-life-defaults"],
    queryFn: () => apiFetch<Record<string, number>>("/equipment/useful-life-defaults"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setPropertyId(defaultPropertyId ?? "");
      setUnitId(NO_SPECIFIC_UNIT_VALUE);
      setCategory("");
      setMake("");
      setModelNumber("");
      setSerialNumber("");
      setCapacity("");
      setFuelType("");
      setSpecificLocation("");
      setDataPlatePhoto(null);
      setUnitPhoto(null);
      setMoreOpen(false);
      setPurchaseDate("");
      setPurchasePrice("");
      setRetailerOrInstaller("");
      setWarrantyType("");
      setWarrantyLength("");
      setWarrantyExpirationDate("");
      setHasExtendedWarranty("");
      setClaimPhoneNumber("");
      setUsefulLifeYears("");
      setUsefulLifeTouched(false);
      setReplacementCostEstimate("");
      setCondition("");
      setServiceInterval("");
      setServiceIntervalMonths("");
      setLastServiceDate("");
      setError(undefined);
    }
  }, [open, defaultPropertyId]);

  // Resets the unit choice back to building-level whenever the property
  // itself changes — a unit id from the PREVIOUS property is never a legal
  // choice for the new one.
  function handlePropertyChange(value: string) {
    setPropertyId(value);
    setUnitId(NO_SPECIFIC_UNIT_VALUE);
  }

  // VC-12.1-04: pre-fills from the seeded useful-life-defaults table for
  // this category, or leaves it blank for a category with none — and never
  // fires at all once the Owner has typed their own value.
  function handleCategoryChange(value: string) {
    setCategory(value);
    if (usefulLifeTouched) {
      return;
    }
    const defaultYears = usefulLifeDefaultsQuery.data?.[value];
    setUsefulLifeYears(defaultYears === undefined ? "" : String(defaultYears));
  }

  function handleUsefulLifeChange(value: string) {
    setUsefulLifeTouched(true);
    setUsefulLifeYears(value);
  }

  const unitOptions = useMemo(() => {
    const real = unitsQuery.data?.data ?? [];
    return [{ value: NO_SPECIFIC_UNIT_VALUE, label: "Building-level (no specific unit)" }, ...real.map((unit) => ({
      value: unit.id,
      label: unit.label,
    }))];
  }, [unitsQuery.data]);

  const canSubmit = propertyId !== "" && category !== "" && !isSubmitting;

  async function uploadPhoto(equipmentId: string, file: File, label: string): Promise<string | undefined> {
    try {
      await apiFetch<DocumentResource>("/documents", {
        method: "POST",
        headers: {
          "X-PropDesk-Filename": encodeURIComponent(file.name),
          "X-PropDesk-Entity-Type": "equipment",
          "X-PropDesk-Entity-Id": equipmentId,
        },
        body: file,
      });
      return undefined;
    } catch (uploadError) {
      return uploadError instanceof ApiError
        ? `${label}: ${uploadError.message}`
        : `${label}: something went wrong uploading it.`;
    }
  }

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const created = await apiFetch<EquipmentResource>("/equipment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prop_id: propertyId,
          unit_id: unitId === NO_SPECIFIC_UNIT_VALUE ? null : unitId,
          category,
          make: make.trim() === "" ? null : make.trim(),
          model_number: modelNumber.trim() === "" ? null : modelNumber.trim(),
          serial_number: serialNumber.trim() === "" ? null : serialNumber.trim(),
          capacity: capacity.trim() === "" ? null : capacity.trim(),
          fuel_type: fuelType.trim() === "" ? null : fuelType.trim(),
          specific_location: specificLocation.trim() === "" ? null : specificLocation.trim(),
          purchase_date: purchaseDate === "" ? null : purchaseDate,
          purchase_price_cents: purchasePrice === "" ? null : Math.round(Number(purchasePrice) * 100),
          retailer_or_installer: retailerOrInstaller.trim() === "" ? null : retailerOrInstaller.trim(),
          warranty_type: warrantyType.trim() === "" ? null : warrantyType.trim(),
          warranty_length: warrantyLength.trim() === "" ? null : warrantyLength.trim(),
          warranty_expiration_date: warrantyExpirationDate === "" ? null : warrantyExpirationDate,
          has_extended_warranty: hasExtendedWarranty === "" ? null : hasExtendedWarranty === "true",
          claim_phone_number: claimPhoneNumber.trim() === "" ? null : claimPhoneNumber.trim(),
          expected_useful_life_years: usefulLifeYears === "" ? null : Number(usefulLifeYears),
          replacement_cost_estimate_cents:
            replacementCostEstimate === "" ? null : Math.round(Number(replacementCostEstimate) * 100),
          condition: condition.trim() === "" ? null : condition.trim(),
          service_interval: serviceInterval.trim() === "" ? null : serviceInterval.trim(),
          service_interval_months: serviceIntervalMonths === "" ? null : Number(serviceIntervalMonths),
          last_service_date: lastServiceDate === "" ? null : lastServiceDate,
        }),
      });

      const uploadErrors: string[] = [];
      if (dataPlatePhoto) {
        const message = await uploadPhoto(created.id, dataPlatePhoto, "Data-plate photo");
        if (message) {
          uploadErrors.push(message);
        }
      }
      if (unitPhoto) {
        const message = await uploadPhoto(created.id, unitPhoto, "Unit photo");
        if (message) {
          uploadErrors.push(message);
        }
      }

      onOpenChange(false);
      onCreated(created.id);
      if (uploadErrors.length > 0) {
        // The equipment record itself saved successfully — a photo upload
        // failure is surfaced but never treated as the whole action having
        // failed (module doc comment).
        setError(uploadErrors.join(" "));
      }
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const propertyOptions = propertiesQuery.data?.data ?? [];
  const categoryOptions = categoriesQuery.data?.data ?? [];

  return (
    // "Log equipment", not "Add equipment" (the button that opens this
    // dialog) — R-E1's own verb, and distinct text from the trigger button
    // so a test (or a screen reader landmark) can tell button and dialog
    // heading apart without relying on DOM structure alone.
    <Container open={open} onOpenChange={onOpenChange} title="Log equipment">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {/* -- Identity + Location — visible immediately (R-E1). ------------ */}
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("prop_id")}</span>
          <Select
            autoFocus={defaultPropertyId === undefined}
            value={propertyId}
            onChange={(event) => handlePropertyChange(event.target.value)}
            disabled={defaultPropertyId !== undefined}
          >
            <option value="">Choose a property…</option>
            {propertyOptions.map((property) => (
              <option key={property.id} value={property.id}>
                {property.nickname}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("unit_id")}</span>
          <Select value={unitId} onChange={(event) => setUnitId(event.target.value)} disabled={propertyId === ""}>
            {unitOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("category")}</span>
          <Select autoFocus={defaultPropertyId !== undefined} value={category} onChange={(event) => handleCategoryChange(event.target.value)}>
            <option value="">Choose a category…</option>
            {categoryOptions.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("serial_number")}</span>
          <Input value={serialNumber} onChange={(event) => setSerialNumber(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">Photo of the data plate</span>
          <Input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(event) => setDataPlatePhoto(event.target.files?.[0] ?? null)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">Photo of the unit</span>
          <Input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(event) => setUnitPhoto(event.target.files?.[0] ?? null)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("make")}</span>
          <Input value={make} onChange={(event) => setMake(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("model_number")}</span>
          <Input value={modelNumber} onChange={(event) => setModelNumber(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("capacity")}</span>
          <Input
            value={capacity}
            onChange={(event) => setCapacity(event.target.value)}
            placeholder="e.g. 40 gal, 3 tons"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("fuel_type")}</span>
          <Input value={fuelType} onChange={(event) => setFuelType(event.target.value)} placeholder="e.g. gas" />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("specific_location")}</span>
          <Input
            value={specificLocation}
            onChange={(event) => setSpecificLocation(event.target.value)}
            placeholder="e.g. basement, unit 2 closet"
          />
        </label>

        {/* -- Purchase/Warranty/Lifecycle/Service — behind "More details". - */}
        <button
          type="button"
          onClick={() => setMoreOpen((value) => !value)}
          aria-expanded={moreOpen}
          className="inline-flex h-tap-target-min items-center self-start text-body font-medium text-accent underline"
        >
          {moreOpen ? "Hide details" : "More details"}
        </button>

        {moreOpen ? (
          <div className="flex flex-col gap-4 border-t border-border-hairline pt-4">
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("purchase_date")}</span>
              <Input type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("purchase_price_cents")}</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={purchasePrice}
                onChange={(event) => setPurchasePrice(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("retailer_or_installer")}</span>
              <Input value={retailerOrInstaller} onChange={(event) => setRetailerOrInstaller(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_type")}</span>
              <Input
                value={warrantyType}
                onChange={(event) => setWarrantyType(event.target.value)}
                placeholder="e.g. parts, labor, full"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_length")}</span>
              <Input value={warrantyLength} onChange={(event) => setWarrantyLength(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("warranty_expiration_date")}</span>
              <Input
                type="date"
                value={warrantyExpirationDate}
                onChange={(event) => setWarrantyExpirationDate(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("has_extended_warranty")}</span>
              <Select value={hasExtendedWarranty} onChange={(event) => setHasExtendedWarranty(event.target.value)}>
                <option value="">Unspecified</option>
                {YES_NO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("claim_phone_number")}</span>
              <Input value={claimPhoneNumber} onChange={(event) => setClaimPhoneNumber(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">
                {labelFor("expected_useful_life_years")}
              </span>
              <Input
                type="number"
                min="0"
                value={usefulLifeYears}
                onChange={(event) => handleUsefulLifeChange(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">
                {labelFor("replacement_cost_estimate_cents")}
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={replacementCostEstimate}
                onChange={(event) => setReplacementCostEstimate(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("condition")}</span>
              <Input value={condition} onChange={(event) => setCondition(event.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("service_interval")}</span>
              <Input
                value={serviceInterval}
                onChange={(event) => setServiceInterval(event.target.value)}
                placeholder="e.g. annual"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("service_interval_months")}</span>
              <Input
                type="number"
                min="1"
                value={serviceIntervalMonths}
                onChange={(event) => setServiceIntervalMonths(event.target.value)}
                placeholder="e.g. 12"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("last_service_date")}</span>
              <Input type="date" value={lastServiceDate} onChange={(event) => setLastServiceDate(event.target.value)} />
            </label>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Saving…" : "Save equipment"}
          </Button>
        </div>
      </form>
    </Container>
  );
}
